import { useState } from 'react';
import { RatingButtons, type SrsRating } from '../../ds';
import { FlashcardBack, FlashcardFront } from '../../components/FlashcardFace';
import { FlipCard } from '../../components/FlipCard';
import { SpeakButton } from '../../components/SpeakButton';
import { ttsTextOf } from '../../components/speech';
import { ErrorState, LoadingState } from '../../components/ScreenState';
import { ProgressBar } from '../../ds';
import { WEB_ICO } from '../../shell/icons';
import { useAsync } from '../../hooks/useAsync';
import { srsApi } from '../../api/srs';
import type { SrsCardView } from '../../api/srs.types';
import { screenShell } from '../lesson/common';
import { RATING_MAP, toButtonIntervals } from './srsRating';

export function SrsReviewScreen({ onExit, onToast }: { onExit: () => void; onToast: (m: string) => void }) {
  const dueQuery = useAsync(() => srsApi.getDueQueue(), []);

  const [queue, setQueue] = useState<SrsCardView[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Nạp dữ liệu vào state cục bộ NGAY TRONG RENDER (không phải useEffect) — chỉ chạy
  // một lần vì guard queue===null tự tắt sau lần set đầu.
  if (dueQuery.data && queue === null) {
    setQueue(dueQuery.data);
    setTotalCount(dueQuery.data.length);
  }

  if (dueQuery.error) return <ErrorState message="Không tải được hàng đợi ôn tập." />;
  if (dueQuery.loading || queue === null) return <LoadingState />;

  const current = queue[0];

  const rate = async (sr: SrsRating) => {
    if (!current || submitting) return;
    setSubmitting(true);
    try {
      const result = await srsApi.submitReview(current.cardId, RATING_MAP[sr], new Date().toISOString());
      // Tiến độ đếm số THẺ đã xong, không đếm số lần bấm: thẻ "Chưa thuộc" quay lại cuối
      // phiên nên chưa tính là xong — nếu không thanh tiến độ vượt quá tổng (37/25).
      if (sr === 'daThuoc') setReviewedCount((c) => c + 1);
      setRevealed(false);
      setQueue((q) => {
        const rest = q!.slice(1);
        // "Chưa thuộc" — FSRS hẹn lại sau ~1-10 phút (bước học lại); dồn thẻ về cuối hàng
        // đợi PHIÊN NÀY để gặp lại ngay thay vì bắt user mở phiên mới sau vài phút.
        return sr === 'chuaThuoc' ? [...rest, { ...current, intervals: result.intervals }] : rest;
      });
    } catch {
      onToast('Không lưu được — thử lại');
    } finally {
      setSubmitting(false);
    }
  };

  if (!current) {
    return (
      <div style={screenShell}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 32 }}>
          <div style={{ width: 76, height: 76, borderRadius: '50%', background: 'var(--success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {WEB_ICO.check('var(--success)', 34)}
          </div>
          <h1 style={{ fontSize: 23, fontWeight: 700, color: 'var(--ink)', textAlign: 'center', margin: 0 }}>
            {totalCount === 0 ? 'Không có thẻ nào cần ôn' : 'Hoàn thành phiên ôn tập!'}
          </h1>
          {totalCount > 0 && <div style={{ fontSize: 16, color: 'var(--text-secondary)' }}>{`Đã ôn ${reviewedCount} thẻ`}</div>}
        </div>
        <div style={{ padding: 20, borderTop: '1px solid var(--border-default)' }}>
          <button
            onClick={onExit}
            style={{
              width: '100%',
              height: 52,
              borderRadius: 'var(--radius-control)',
              background: 'var(--cta-default)',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-vi)',
              fontWeight: 600,
              fontSize: 16,
            }}
          >
            Về Ôn tập
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={screenShell}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px 10px' }}>
        <button
          onClick={onExit}
          aria-label="Đóng"
          style={{ width: 44, height: 44, marginLeft: -10, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {WEB_ICO.close('var(--ink)', 24)}
        </button>
        <div style={{ flex: 1 }}>
          <ProgressBar value={reviewedCount} max={Math.max(1, totalCount)} />
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', minWidth: 40, textAlign: 'right' }}>{`${reviewedCount}/${totalCount}`}</div>
      </div>

      <div style={{ flex: 1, display: 'flex', padding: 24, minHeight: 0, overflow: 'hidden' }}>
        <FlipCard
          key={current.cardId}
          flipped={revealed}
          onClick={() => setRevealed((r) => !r)}
          onSwipe={(direction) => rate(direction === 'right' ? 'daThuoc' : 'chuaThuoc')}
          swipeDisabled={submitting}
          front={<FlashcardFront item={current.item} />}
          back={<FlashcardBack item={current.item} />}
          overlay={<SpeakButton text={ttsTextOf(current.item)} variant="plain" size={48} />}
        />
      </div>

      <div style={{ padding: 20, borderTop: '1px solid var(--border-default)' }}>
        <RatingButtons
          onRate={rate}
          disabled={submitting}
          intervalMinutes={toButtonIntervals(current.intervals)}
          icons={{ daThuoc: WEB_ICO.check('var(--cta-default)', 18) }}
        />
      </div>
    </div>
  );
}
